(() => {
  const doc = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  doc.classList.add('awk-motion-ready');

  const setRevealDelays = (root = document) => {
    root.querySelectorAll('[data-awk-stagger]').forEach((group) => {
      [...group.children].forEach((item, index) => {
        if (!item.hasAttribute('data-awk-reveal')) item.setAttribute('data-awk-reveal', '');
        item.style.setProperty('--awk-delay', `${Math.min(index * 90, 540)}ms`);
      });
    });
  };

  let revealObserver;
  const observeReveals = (root = document) => {
    setRevealDelays(root);
    const items = root.querySelectorAll('[data-awk-reveal]:not(.is-visible)');
    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    items.forEach((item) => revealObserver.observe(item));
  };

  const getScrollMetrics = () => {
    const wrapper = document.querySelector('.page-wrapper');
    const visualViewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
    const windowTop = Math.max(
      window.scrollY || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0
    );
    const windowHeight = Math.max(
      document.documentElement.scrollHeight || 0,
      document.body.scrollHeight || 0
    );
    const windowMax = Math.max(windowHeight - visualViewportHeight, 0);
    const wrapperTop = wrapper?.scrollTop || 0;
    const wrapperViewport = wrapper?.clientHeight || visualViewportHeight;
    const wrapperMax = wrapper
      ? Math.max(wrapper.scrollHeight - wrapperViewport, 0)
      : 0;
    const windowRatio = windowMax > 0 ? Math.min(Math.max(windowTop / windowMax, 0), 1) : 0;
    const wrapperRatio = wrapperMax > 0 ? Math.min(Math.max(wrapperTop / wrapperMax, 0), 1) : 0;
    const usesWrapper = wrapperMax > 0 && wrapperRatio >= windowRatio;

    return {
      wrapper,
      container: usesWrapper ? wrapper : (document.scrollingElement || document.documentElement),
      usesWrapper,
      top: usesWrapper ? wrapperTop : windowTop,
      max: usesWrapper ? wrapperMax : windowMax,
      viewport: visualViewportHeight,
      ratio: Math.max(windowRatio, wrapperRatio),
      source: wrapperRatio > windowRatio ? 'wrapper' : 'window'
    };
  };

  const updateScrollState = () => {
    const metrics = getScrollMetrics();
    const ratio = metrics.ratio;
    const progress = document.querySelector('.awk-motion-progress__bar');
    if (progress) progress.style.width = `${ratio * 100}%`;
    doc.classList.toggle('awk-header-scrolled', metrics.top > 36);

    const scrollTopButton = document.querySelector('[data-awk-scroll-top]');
    if (!scrollTopButton) return;

    const hasScrollablePage = metrics.max > 0 || ratio > 0;
    const shouldShowScrollTop = hasScrollablePage && ratio >= 0.5;
    const mobileViewport = window.matchMedia('(max-width: 990px), (hover: none) and (pointer: coarse)').matches;
    const previewBarHeight = document.getElementById('PBarNextFrameWrapper')?.getBoundingClientRect().height || 0;
    const baseOffset = mobileViewport ? 96 : 48;
    const previewOffset = previewBarHeight > 0
      ? previewBarHeight + (mobileViewport ? 44 : 30)
      : baseOffset;
    const stickyBuy = document.querySelector('[data-awk-sticky-buy].is-visible');
    const stickyOffset = stickyBuy
      ? Math.max(metrics.viewport, window.innerHeight || 0)
        - stickyBuy.getBoundingClientRect().top
        + (mobileViewport ? 42 : 36)
      : baseOffset;
    const finalOffset = Math.ceil(Math.max(baseOffset, previewOffset, stickyOffset));

    scrollTopButton.removeAttribute('hidden');
    scrollTopButton.classList.toggle('is-visible', shouldShowScrollTop);
    scrollTopButton.setAttribute('aria-hidden', shouldShowScrollTop ? 'false' : 'true');
    scrollTopButton.setAttribute('tabindex', shouldShowScrollTop ? '0' : '-1');
    scrollTopButton.dataset.awkScrollSource = metrics.source;
    scrollTopButton.style.setProperty('--awk-scroll-top-bottom', `${finalOffset}px`);
    scrollTopButton.style.setProperty('position', 'fixed', 'important');
    scrollTopButton.style.setProperty('z-index', '2147483001', 'important');
    scrollTopButton.style.setProperty('display', 'grid', 'important');
    scrollTopButton.style.setProperty(
      'bottom',
      `calc(${finalOffset}px + env(safe-area-inset-bottom))`,
      'important'
    );
    scrollTopButton.style.setProperty('opacity', shouldShowScrollTop ? '1' : '0', 'important');
    scrollTopButton.style.setProperty('visibility', shouldShowScrollTop ? 'visible' : 'hidden', 'important');
    scrollTopButton.style.setProperty('pointer-events', shouldShowScrollTop ? 'auto' : 'none', 'important');
    scrollTopButton.style.setProperty(
      'transform',
      shouldShowScrollTop ? 'translateY(0)' : 'translateY(18px)',
      'important'
    );
  };

  const initScrollToTop = () => {
    const button = document.querySelector('[data-awk-scroll-top]');
    if (!button || button.dataset.awkScrollTopReady === 'true') return;
    button.dataset.awkScrollTopReady = 'true';

    button.addEventListener('click', () => {
      const metrics = getScrollMetrics();
      const behavior = reduced ? 'auto' : 'smooth';
      const scrollElement = (element) => {
        if (!element) return;
        try {
          element.scrollTo({ top: 0, behavior });
        } catch (error) {
          element.scrollTop = 0;
        }
      };

      scrollElement(metrics.wrapper);
      scrollElement(document.scrollingElement);
      window.scrollTo({ top: 0, behavior });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  };

  let scrollTicking = false;
  const requestScrollUpdate = () => {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(() => {
      updateScrollState();
      scrollTicking = false;
    });
  };

  window.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.addEventListener('resize', requestScrollUpdate, { passive: true });
  window.addEventListener('orientationchange', requestScrollUpdate, { passive: true });
  window.addEventListener('awk:sticky-buy-change', requestScrollUpdate);
  document.querySelector('.page-wrapper')?.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.visualViewport?.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.visualViewport?.addEventListener('resize', requestScrollUpdate, { passive: true });

  const initHeroMotion = (root = document) => {
    if (reduced || !window.matchMedia('(pointer:fine)').matches) return;
    root.querySelectorAll('.awk-motion-hero__stage:not([data-awk-motion-bound])').forEach((stage) => {
      stage.setAttribute('data-awk-motion-bound', 'true');
      stage.addEventListener('pointermove', (event) => {
        const rect = stage.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width;
        const y = (event.clientY - rect.top) / rect.height;
        stage.style.setProperty('--awk-parallax-x', `${(x - .5) * 18}px`);
        stage.style.setProperty('--awk-parallax-y', `${(y - .5) * 18}px`);
        stage.style.setProperty('--awk-pointer-x', `${x * 100}%`);
        stage.style.setProperty('--awk-pointer-y', `${y * 100}%`);
      });
      stage.addEventListener('pointerleave', () => {
        stage.style.setProperty('--awk-parallax-x', '0px');
        stage.style.setProperty('--awk-parallax-y', '0px');
        stage.style.setProperty('--awk-pointer-x', '50%');
        stage.style.setProperty('--awk-pointer-y', '45%');
      });
    });
  };

  const initMegaMenu = (root = document) => {
    root.querySelectorAll('[data-awk-mega-root]:not([data-awk-mega-bound])').forEach((mega) => {
      mega.setAttribute('data-awk-mega-bound', 'true');

      const triggers = [...mega.querySelectorAll('[data-awk-mega-trigger]')];
      const panels = [...mega.querySelectorAll('[data-awk-mega-panel]')];
      const menuBar = mega.querySelector('.awk-mega__bar');
      const overlay = mega.querySelector('[data-awk-mega-overlay]');
      const mobileToggle = mega.querySelector('[data-awk-mobile-menu-toggle]');
      const mobileDrawer = mega.querySelector('[data-awk-mobile-menu-drawer]');
      const mobileClose = mega.querySelector('[data-awk-mobile-menu-close]');
      let closeTimer;

      const closeDesktop = () => {
        window.clearTimeout(closeTimer);
        triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
        panels.forEach((panel) => {
          panel.classList.remove('is-open');
          panel.setAttribute('aria-hidden', 'true');
        });
        mega.classList.remove('is-desktop-open');
      };

      const scheduleDesktopClose = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          const barHovered = menuBar?.matches(':hover');
          const panelHovered = panels.some((panel) => panel.matches(':hover'));
          if (!barHovered && !panelHovered) closeDesktop();
        }, 170);
      };

      const keepDesktopOpen = () => window.clearTimeout(closeTimer);

      const openDesktop = (trigger) => {
        const targetId = trigger.getAttribute('data-awk-mega-trigger');
        const target = panels.find((panel) => panel.id === targetId);
        if (!target) return;
        window.clearTimeout(closeTimer);
        triggers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
        panels.forEach((panel) => {
          const active = panel === target;
          panel.classList.toggle('is-open', active);
          panel.setAttribute('aria-hidden', String(!active));
        });
        mega.classList.add('is-desktop-open');
      };

      panels.forEach((panel) => panel.setAttribute('aria-hidden', 'true'));
      triggers.forEach((trigger) => {
        trigger.addEventListener('pointerenter', () => openDesktop(trigger));
        trigger.addEventListener('focus', () => openDesktop(trigger));
        trigger.addEventListener('click', () => openDesktop(trigger));
      });
      menuBar?.addEventListener('pointerenter', keepDesktopOpen);
      menuBar?.addEventListener('pointerleave', scheduleDesktopClose);
      panels.forEach((panel) => {
        panel.addEventListener('pointerenter', keepDesktopOpen);
        panel.addEventListener('pointerleave', scheduleDesktopClose);
      });
      overlay?.addEventListener('click', closeDesktop);

      const closeMobile = () => {
        mobileToggle?.setAttribute('aria-expanded', 'false');
        mobileDrawer?.classList.remove('is-open');
        mobileDrawer?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('awk-mobile-menu-open');
      };

      const openMobile = () => {
        closeDesktop();
        mobileToggle?.setAttribute('aria-expanded', 'true');
        mobileDrawer?.classList.add('is-open');
        mobileDrawer?.setAttribute('aria-hidden', 'false');
        document.body.classList.add('awk-mobile-menu-open');
      };

      mobileToggle?.addEventListener('click', () => {
        if (mobileDrawer?.classList.contains('is-open')) closeMobile();
        else openMobile();
      });
      mobileClose?.addEventListener('click', closeMobile);

      mega.querySelectorAll('[data-awk-mobile-accordion]').forEach((button) => {
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (!panel) return;
        panel.setAttribute('aria-hidden', 'true');
        button.addEventListener('click', () => {
          const willOpen = button.getAttribute('aria-expanded') !== 'true';
          button.setAttribute('aria-expanded', String(willOpen));
          panel.classList.toggle('is-open', willOpen);
          panel.setAttribute('aria-hidden', String(!willOpen));
        });
      });

      mega.querySelectorAll('.awk-mega-mobile__drawer a').forEach((link) => link.addEventListener('click', closeMobile));
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        closeDesktop();
        closeMobile();
      });
      window.matchMedia('(min-width: 991px)').addEventListener('change', closeMobile);
    });
  };

  const init = (root = document) => {
    observeReveals(root);
    initHeroMotion(root);
    initMegaMenu(root);
    initScrollToTop();
    updateScrollState();
    window.setTimeout(requestScrollUpdate, 250);
    window.setTimeout(requestScrollUpdate, 1200);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();

